/**
 * Kurze Sätze zu einer Reinigungsaufgabe für Plan, Aufgabe und Blatt. Daten und Uhrzeiten sind
 * Ortszeit des Objekts, wie der Server sie liefert; „heute“ ist das Heute des Objekts.
 */
import type { CleaningTaskQueueResponse } from '@/shared/api';
import { addDaysIso } from '../calendar/cleaningCalendarHelpers';

type Task = CleaningTaskQueueResponse;

const shortDate = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.`;

/** „fällig 10:00–12:00“; reicht das Fenster in einen anderen Tag, steht dessen Datum dabei. */
export function windowLabel(task: Task): string {
  const from = task.scheduledFromLocalTime.slice(0, 5);
  const to = task.scheduledToLocalTime?.slice(0, 5);
  if (!to) return `ab ${from}`;
  const toDate = task.scheduledToLocalDate;
  return !toDate || toDate === task.scheduledFromLocalDate
    ? `fällig ${from}–${to}`
    : `fällig ${from} – ${shortDate(toDate)} ${to}`;
}

/** „Anreise heute 15:00“, „Anreise morgen 16:00“, „Anreise 24.10. 16:00“ — null ohne Anreise. */
export function arrivalShort(task: Task, today: string): string | null {
  const arrival = task.nextArrival;
  if (!arrival) return null;
  const day =
    arrival.checkInDate === today
      ? 'heute'
      : arrival.checkInDate === addDaysIso(today, 1)
        ? 'morgen'
        : shortDate(arrival.checkInDate);
  return `Anreise ${day} ${arrival.checkInTime.slice(0, 5)}`;
}

/** Die nächste Anreise fällt auf den Tag der Reinigung — enges Fenster. */
export const isSameDayArrival = (task: Task) =>
  task.nextArrival !== null && task.nextArrival.checkInDate === task.scheduledFor;

/** „6 Fotos · 2 von 6“ oder „ohne Fotos“ — die Räume legt der Server beim Anlegen fest. */
export function proofLabel(task: Task): string {
  return task.photoSlotCount === 0
    ? 'ohne Fotos'
    : `${task.photoSlotCount} Fotos · ${task.filledPhotoSlotCount} von ${task.photoSlotCount}`;
}

/**
 * Uhrzeit eines echten Zeitpunkts (UTC) in der Zeitzone des Objekts. Ist sie unbekannt oder
 * ungültig, steht die Gerätezeit da — und das dabei, statt still eine andere Uhr zu zeigen.
 */
export function clockTime(iso: string, timeZone?: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '–';
  const format = (zone?: string) =>
    new Intl.DateTimeFormat('de-DE', { hour: '2-digit', minute: '2-digit', timeZone: zone }).format(
      date,
    );
  if (!timeZone) return `${format()} (Gerätezeit)`;
  try {
    return format(timeZone);
  } catch {
    return `${format()} (Gerätezeit)`;
  }
}
